// This needs to be at the top of the file so imports work correctly
import '../styles/globals.scss';

import { ApolloProvider } from '@apollo/client';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useThemeWithLocale from 'hooks/useThemeWithLocale';
import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import { AppProps } from 'next/app';
import { SnackbarProvider } from 'notistack';
import { apolloClient } from 'server/apolloClient';
import { apolloClientServer } from 'server/apolloClientServer';
import RefreshState from 'state/RefreshState';
import useSetGlobalState from 'state/useSetGlobalState';
import createEmotionCache from '../utils/createEmotionCache';

const clientSideEmotionCache = createEmotionCache();

export const currentApolloClient = typeof window === 'undefined' ? apolloClientServer('se') : apolloClient();

function App(props: AppProps & { emotionCache?: EmotionCache | undefined; session: Session }) {
    const { Component, emotionCache = clientSideEmotionCache, pageProps, session } = props;

    useSetGlobalState(pageProps.globalState);
    const queryClient = new QueryClient();

    // eslint-disable-next-line react/destructuring-assignment
    const themeWithLocale = useThemeWithLocale();

    return (
        <QueryClientProvider client={queryClient}>
            <SessionProvider session={session}>
                <CacheProvider value={emotionCache}>
                    <ThemeProvider theme={themeWithLocale}>
                        <CssBaseline />
                        <ApolloProvider client={currentApolloClient}>
                            <SnackbarProvider
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                            >
                                <RefreshState />
                                <Component {...pageProps} options={{ basePath: '/src/api/auth' }} />
                            </SnackbarProvider>
                        </ApolloProvider>
                    </ThemeProvider>
                </CacheProvider>
            </SessionProvider>
        </QueryClientProvider>
    );
}

export default appWithTranslation(App);
